import {
  CanAccess,
  useCanAccess,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { EditButton } from "@/components/admin/edit-button";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";

import { TestDataBadge } from "../deals/TestDataBadge";
import { StatusPill } from "../design/StatusPill";
import type { Project } from "../types";
import { ComplianceStatusPill } from "./ComplianceStatusPill";

/**
 * Renders a project's `archived` flag as a StatusPill (Almikia Epic E3b-2,
 * "Archive Mazarik"; design-system.md section 3: binary states always render
 * as a green/red semaforo with icon and text, never color alone). Archived
 * projects are kept in the list (history kept, never deleted), just clearly
 * marked.
 */
const ArchivedStatusPill = () => {
  const record = useRecordContext<Project>();
  const translate = useTranslate();
  if (!record) return null;

  return record.archived ? (
    <StatusPill
      tone="not-ok"
      text={translate("resources.projects.archived_status.archived")}
    />
  ) : (
    <StatusPill
      tone="ok"
      text={translate("resources.projects.archived_status.active")}
    />
  );
};

/** Project name, plus the "Datos de prueba" badge on the DEMO project (direccion only). */
const ProjectNameCell = () => {
  const record = useRecordContext<Project>();
  if (!record) return null;

  return (
    <span className="flex items-center gap-2">
      {record.name}
      <TestDataBadge isTestData={record.is_demo} />
    </span>
  );
};

/**
 * ASSUMPTION: no create action here — project codes are constrained to the
 * 6 known values already seeded in the database (TASK-001), so a "new
 * project" flow would only invite invalid codes.
 */
export function ProjectList() {
  const { canAccess: canEdit, isPending } = useCanAccess({
    resource: "projects",
    action: "edit",
  });

  if (isPending) return null;

  return (
    <List sort={{ field: "code", order: "ASC" }} perPage={25}>
      <DataTable rowClick={canEdit ? "edit" : false}>
        <DataTable.Col source="name">
          <ProjectNameCell />
        </DataTable.Col>
        <DataTable.Col source="code" />
        <DataTable.Col source="developer_id">
          {/* TASK-007: developers have no top-level nav tab, so the project
              list's developer name is how everyone reaches the developers
              list (every role can view it; DeveloperList/DeveloperEdit's own
              canAccess still gates the edit affordance to direccion). */}
          <ReferenceField
            source="developer_id"
            reference="developers"
            link="list"
          >
            <TextField source="legal_name" />
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col source="compliance_pack_status">
          <ComplianceStatusPill />
        </DataTable.Col>
        <DataTable.Col source="archived">
          <ArchivedStatusPill />
        </DataTable.Col>
        <DataTable.Col source="invoice_concept_template_version" />
        <DataTable.Col label={false}>
          <CanAccess resource="projects" action="edit">
            <EditButton />
          </CanAccess>
        </DataTable.Col>
      </DataTable>
    </List>
  );
}
